import * as React from 'react';

import { PageProps, graphql } from 'gatsby';
import { ImageDataLike, StaticImage, getImage } from 'gatsby-plugin-image';
import invariant from 'tiny-invariant';

import { team } from 'stile-shared/data/team';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { MediaBlock } from 'stile-shared/src/components/2020/blocks/MediaBlock';
import { TeamBlock } from 'stile-shared/src/components/2020/blocks/TeamBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { P } from 'stile-shared/src/components/2020/common/Primitives';

import { getPageData } from 'utils/getPageData';

import { PageLayout } from 'templates/2020/PageLayout';

import { NextPageBlock } from 'components/blocks/NextPageBlock';

export { Head } from 'templates/2020/Head';

type OurTeamPageProps = PageProps & {
  data: Queries.TeamImagesQuery;
};

function OurTeamPage({ data, ...props }: OurTeamPageProps) {
  const leadershipTeam = getLeadershipTeam(team, data);
  const staffTeam = getStaffTeam(team, data);
  const { hero } = getPageData(props.location.pathname);

  return (
    <PageLayout {...props}>
      <HeroBlock
        {...hero}
        media={
          <MediaBlock as="div" layout="montage-five">
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/our-team/our_team_01.png"
              alt="A selfie of four Stile team members wearing ski goggles and helmets"
            />
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/our-team/our_team_02.png"
              alt="A support staff member wearing a call headset and smiling"
            />
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/our-team/our_team_03.png"
              alt="A software engineer at a workstation with a laptop and a wide external monitor"
            />
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/our-team/our_team_04.png"
              alt="A closeup of someone's desk. Headphones and a cupcake-shaped squeeze toy are in focus"
            />
            <StaticImage
              loading="eager"
              src="../../../../stile-shared/assets/images/our-team/our_team_05.png"
              alt="A selfie of four team members, two of whom are holding beers. All four are smiling at the camera"
            />
          </MediaBlock>
        }
      />

      <TextBlock>
        <P>
          Every aspect of Stile is crafted in-house by our passionate team of teachers, scientists,
          artists and engineers — a multidisciplinary group of the best and brightest. You'll find
          Stilists scattered all around Australia, New Zealand and the United States with our global
          Headquarters based in Melbourne, Australia and US Headquarters in Portland, Oregon.
        </P>
      </TextBlock>

      <TeaserBlock
        variant="dark"
        title="Meet the Stile team"
        text="We asked the team how they would describe a Stile person. Here is what they had to say."
        video={{
          text: 'Watch (3 mins)',
          url: 'https://www.youtube.com/embed/tehcTUqr9t4',
          coverImage: (
            <StaticImage
              src="../../../../stile-shared/assets/images/our-team/culture_video_cover.jpg"
              alt="Several Stile staff members sitting in a relaxed meeting area. One is explaining something and the others are listening attentively"
            />
          ),
        }}
      />

      <TeamBlock variant="large" title="Leadership" items={leadershipTeam} />

      <TeamBlock title="The dream team" items={staffTeam} />

      <NextPageBlock path="/who-we-are/join-the-team/" />

      <CtaBlock />
    </PageLayout>
  );
}

function getImageFromQueryData(image: string, imagesData: OurTeamPageProps['data']) {
  const imageData = getImage(
    imagesData.allFile.edges?.find(({ node }) => node?.base === image)
      ?.node as unknown as ImageDataLike
  );

  invariant(imageData, `Unable to find image for source ${image}`);

  return imageData;
}

function getLeadershipTeam(teamData: typeof team, imagesData: OurTeamPageProps['data']) {
  // `keyLeadership` should include the names of any staff to be 'hoisted' to the start of
  // the leadership section, in the order they should be displayed
  const keyLeadership = ['Daniel Pikler', 'Byron Scaf'];

  const mapMember = ({ name, role, background, image }: (typeof teamData)[number]) => ({
    name,
    role,
    background,
    image: getImageFromQueryData(image, imagesData),
  });

  const founders = teamData
    .filter(({ name }) => keyLeadership.includes(name))
    .sort((a, b) => keyLeadership.indexOf(a.name) - keyLeadership.indexOf(b.name))
    .map(mapMember);

  const otherLeadership = teamData
    .filter(
      ({ categories, name }) => categories.includes('leadership') && !keyLeadership.includes(name)
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(mapMember);

  return [...founders, ...otherLeadership];
}

function getStaffTeam(teamData: typeof team, imagesData: OurTeamPageProps['data']) {
  return teamData
    .filter(({ categories }) => !categories.includes('leadership'))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ name, role, image }) => ({
      name,
      role,
      image: getImageFromQueryData(image, imagesData),
    }));
}

export const query = graphql`
  query TeamImages {
    allFile(
      filter: { sourceInstanceName: { eq: "images" }, relativePath: { glob: "*/portraits/*" } }
    ) {
      edges {
        node {
          base
          childImageSharp {
            gatsbyImageData(width: 300)
          }
        }
      }
    }
  }
`;

export default OurTeamPage;
